import React, { useState, useEffect } from 'react';
import {
  Package,
  Plus,
  Search,
  Edit,
  Trash2,
  AlertCircle,
  Key,
  Cpu,
  Radio,
  Wrench,
  Box,
  DollarSign,
  Loader2,
  Image as ImageIcon
} from 'lucide-react';
import { inventoryAPI } from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import ImageUpload from '../components/ImageUpload';
import Combobox from '../components/Combobox';

const CATEGORIES = [
  { id: 'all', label: 'All Items', icon: Box, color: 'text-blue-500' },
  { id: 'key_blank', label: 'Key Blanks', icon: Key, color: 'text-blue-500' },
  { id: 'transponder', label: 'Transponders', icon: Cpu, color: 'text-purple-500' },
  { id: 'remote', label: 'Remotes', icon: Radio, color: 'text-green-500' },
  { id: 'part', label: 'Parts', icon: Package, color: 'text-orange-500' },
  { id: 'tool', label: 'Tools', icon: Wrench, color: 'text-pink-500' },
];

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [statDrilldown, setStatDrilldown] = useState(null); // { title, filter, items }

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'key_blank',
    sku: '',
    quantity: 0,
    min_quantity: 0,
    unit_cost: 0,
    unit_price: 0,
    supplier: '',
    location: '',
    image_url: null,
  });

  useEffect(() => {
    loadInventory();
  }, []);

  useEffect(() => {
    filterItems();
  }, [items, searchQuery, selectedCategory]);

  const loadInventory = async () => {
    setLoading(true);
    try {
      const data = await inventoryAPI.list();
      setItems(data || []);
    } catch (error) {
      console.error('Error loading inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = items;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((item) => item.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name?.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.sku?.toLowerCase().includes(query)
      );
    }

    setFilteredItems(filtered);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name || '',
        description: item.description || '',
        category: item.category || 'key_blank',
        sku: item.sku || '',
        quantity: item.quantity || 0,
        min_quantity: item.min_quantity || 0,
        unit_cost: item.unit_cost || 0,
        unit_price: item.unit_price || 0,
        supplier: item.supplier || '',
        location: item.location || '',
        image_url: item.image_url || null,
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        description: '',
        category: 'key_blank',
        sku: '',
        quantity: 0,
        min_quantity: 0,
        unit_cost: 0,
        unit_price: 0,
        supplier: '',
        location: '',
        image_url: null,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Item name is required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingItem) {
        await inventoryAPI.update(editingItem.id, formData);
        toast.success('Item updated successfully!');
      } else {
        await inventoryAPI.create(formData);
        toast.success('Item added successfully!');
      }
      setDialogOpen(false);
      loadInventory();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Failed to save item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await inventoryAPI.delete(deleteConfirm.id);
      toast.success('Item deleted successfully!');
      setDeleteConfirm(null);
      loadInventory();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find((c) => c.id === category);
    return cat ? cat.icon : Package;
  };

  const getCategoryColor = (category) => {
    const cat = CATEGORIES.find((c) => c.id === category);
    return cat ? cat.color : 'text-gray-500';
  };

  const totalValue = items.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0);
  const totalRetailValue = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const lowStockCount = items.filter((item) => item.quantity <= item.min_quantity).length;

  return (
    <div className="space-y-6 fade-in" data-testid="inventory-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Inventory Management</h1>
          <p className="text-muted-foreground">Track keys, transponders, remotes, parts, and tools</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="btn-gradient-primary"
          data-testid="add-item-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Stats Cards — click any to drill into a filtered list */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card
          className="stat-blue border cursor-pointer card-hover transition hover:scale-[1.02]"
          onClick={() => setStatDrilldown({ title: 'All Inventory Items', items: items })}
          data-testid="stat-total-items"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Items</p>
                <p className="text-2xl font-bold">{items.length}</p>
              </div>
              <Box className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card
          className="stat-green border cursor-pointer card-hover transition hover:scale-[1.02]"
          onClick={() => setStatDrilldown({
            title: 'Items by Cost Value',
            items: [...items].sort((a, b) => (b.quantity * b.unit_cost) - (a.quantity * a.unit_cost)),
          })}
          data-testid="stat-total-cost"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Cost Value</p>
                <p className="text-2xl font-bold">{formatCurrency(totalValue)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card
          className="stat-purple border cursor-pointer card-hover transition hover:scale-[1.02]"
          onClick={() => setStatDrilldown({
            title: 'Items by Retail Value',
            items: [...items].sort((a, b) => (b.quantity * b.unit_price) - (a.quantity * a.unit_price)),
          })}
          data-testid="stat-retail-value"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Retail Value</p>
                <p className="text-2xl font-bold">{formatCurrency(totalRetailValue)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card
          className="stat-orange border cursor-pointer card-hover transition hover:scale-[1.02]"
          onClick={() => setStatDrilldown({
            title: 'Low Stock Items',
            items: items.filter((i) => i.quantity <= i.min_quantity),
          })}
          data-testid="stat-low-stock"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Low Stock Alerts</p>
                <p className="text-2xl font-bold">{lowStockCount}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, SKU, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>

          <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
            <TabsList className="w-full justify-start overflow-x-auto no-scrollbar">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <TabsTrigger key={cat.id} value={cat.id} className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${cat.color}`} />
                    {cat.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* Items Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="h-32 bg-muted rounded mb-3 shimmer" />
                <div className="h-4 bg-muted rounded mb-2 shimmer" />
                <div className="h-4 bg-muted rounded w-2/3 shimmer" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No items found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || selectedCategory !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by adding your first inventory item'}
            </p>
            <Button onClick={() => handleOpenDialog()} className="btn-gradient-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add First Item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item) => {
            const Icon = getCategoryIcon(item.category);
            const isLowStock = item.quantity <= item.min_quantity;

            return (
              <Card
                key={item.id}
                className="card-hover overflow-hidden"
                data-testid={`inventory-item-${item.id}`}
              >
                {/* Image */}
                <div className="h-40 bg-muted/30 relative overflow-hidden">
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon className={`w-16 h-16 ${getCategoryColor(item.category)} opacity-50`} />
                    </div>
                  )}
                  {isLowStock && (
                    <Badge variant="destructive" className="absolute top-2 right-2">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Low Stock
                    </Badge>
                  )}
                </div>

                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1 line-clamp-1">{item.name}</h3>
                      {item.sku && (
                        <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="capitalize">
                      <Icon className={`w-3 h-3 mr-1 ${getCategoryColor(item.category)}`} />
                      {item.category?.replace('_', ' ')}
                    </Badge>
                  </div>

                  {item.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {item.description}
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Quantity</p>
                      <p className="font-semibold">
                        {item.quantity}
                        {isLowStock && <span className="text-destructive ml-1">⚠</span>}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Price</p>
                      <p className="font-semibold">{formatCurrency(item.unit_price)}</p>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-3 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenDialog(item)}
                      className="flex-1"
                      data-testid={`edit-item-${item.id}`}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDeleteConfirm(item)}
                      className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      data-testid={`delete-item-${item.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Update item details' : 'Add a new item to your inventory'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="name">Item Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., HU64 Mercedes Key Blank"
                  required
                  data-testid="item-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Combobox
                  testid="category-select"
                  value={formData.category}
                  onChange={(val) => setFormData({ ...formData, category: val })}
                  placeholder="Type or select category..."
                  options={CATEGORIES.filter((c) => c.id !== 'all').map((c) => ({ value: c.id, label: c.label }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sku">SKU</Label>
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="e.g., HU64-MB"
                  data-testid="sku-input"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Item description, notes, programming info..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                  data-testid="quantity-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="min_quantity">Min Stock Alert</Label>
                <Input
                  id="min_quantity"
                  type="number"
                  min="0"
                  value={formData.min_quantity}
                  onChange={(e) => setFormData({ ...formData, min_quantity: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="unit_cost">Unit Cost ($)</Label>
                <Input
                  id="unit_cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.unit_cost}
                  onChange={(e) => setFormData({ ...formData, unit_cost: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="unit_price">Unit Price ($)</Label>
                <Input
                  id="unit_price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="supplier">Supplier</Label>
                <Combobox
                  testid="supplier-select"
                  value={formData.supplier}
                  onChange={(val) => setFormData({ ...formData, supplier: val })}
                  placeholder="Type supplier name..."
                  allowCustom
                  options={Array.from(new Set(items.map((i) => i.supplier).filter(Boolean)))
                    .sort()
                    .map((s) => ({ value: s, label: s }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Storage Location</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Shelf A-12"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Item Image</Label>
                <ImageUpload
                  value={formData.image_url}
                  onChange={(img) => setFormData({ ...formData, image_url: img })}
                  label="Upload Item Image"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-gradient-primary" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingItem ? (
                  'Update Item'
                ) : (
                  'Add Item'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="btn-gradient-danger">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stat drill-down dialog — opens when a stat card is clicked */}
      <Dialog open={!!statDrilldown} onOpenChange={(o) => !o && setStatDrilldown(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="stat-drilldown-dialog">
          <DialogHeader>
            <DialogTitle>{statDrilldown?.title}</DialogTitle>
            <DialogDescription>
              {statDrilldown?.items?.length || 0} items — showing detailed breakdown.
            </DialogDescription>
          </DialogHeader>
          {statDrilldown?.items?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Nothing to show here.</p>
          ) : (
            <div className="overflow-auto max-h-[60vh] border rounded">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3">Name</th>
                    <th className="text-left px-3">Category</th>
                    <th className="text-left px-3">SKU</th>
                    <th className="text-right px-3">Qty</th>
                    <th className="text-right px-3">Min</th>
                    <th className="text-right px-3">Cost</th>
                    <th className="text-right px-3">Retail</th>
                    <th className="text-right px-3">Stock Value</th>
                  </tr>
                </thead>
                <tbody>
                  {statDrilldown?.items?.slice(0, 200).map((it) => (
                    <tr key={it.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-2 px-3 font-medium">{it.name}</td>
                      <td className="px-3 text-muted-foreground">{it.category}</td>
                      <td className="px-3 font-mono text-xs">{it.sku || '-'}</td>
                      <td className="px-3 text-right">{it.quantity}</td>
                      <td className="px-3 text-right text-muted-foreground">{it.min_quantity}</td>
                      <td className="px-3 text-right">{formatCurrency(it.unit_cost)}</td>
                      <td className="px-3 text-right">{formatCurrency(it.unit_price)}</td>
                      <td className="px-3 text-right font-semibold">{formatCurrency(it.quantity * it.unit_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
